import api from "./api";
import { asList, parsePaginatedList } from "../utils/pagination";

export interface SalesPoint {
  id: string;
  name: string;
  description: string | null;
  address: string | null;
  gstin: string | null;
  /** Free-form any licence string */
  licence: string | null;
  phoneNo: string | null;
  message: string | null;
  /** CGST_SGST | IGST */
  taxMode: string;
  /** restaurant | retail */
  workstationType: string;
  branchId: string;
  branch?: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateSalesPointData {
  name: string;
  description?: string | null;
  address?: string | null;
  gstin?: string | null;
  licence?: string | null;
  phoneNo?: string | null;
  message?: string | null;
  taxMode?: string;
  workstationType?: string;
  branchId: string;
}

export interface UpdateSalesPointData {
  name?: string;
  description?: string | null;
  address?: string | null;
  gstin?: string | null;
  licence?: string | null;
  phoneNo?: string | null;
  message?: string | null;
  taxMode?: string;
  workstationType?: string;
  branchId?: string;
}

export interface SalesPointStats {
  totalUsers: number;
  totalCardHolders: number;
  totalTransactions: number;
  totalRevenue: number;
}

const salesPointAPI = {
  getSalesPoints: async (params?: {
    pageNo?: number;
    pageSize?: number;
    search?: string;
    branchId?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<{ data: SalesPoint[]; total: number }> => {
    const response = await api.get("/sales-points", { params });
    const { data, total } = parsePaginatedList<SalesPoint>(response.data.data);
    return { data, total };
  },

  getAllSalesPoints: async (): Promise<SalesPoint[]> => {
    const response = await api.get("/sales-points/all");
    return asList<SalesPoint>(response.data.data);
  },

  getSalesPointById: async (id: string): Promise<SalesPoint> => {
    const response = await api.get(`/sales-points/${id}`);
    return response.data.data;
  },

  createSalesPoint: async (data: CreateSalesPointData): Promise<SalesPoint> => {
    const response = await api.post("/sales-points", data);
    return response.data.data;
  },

  updateSalesPoint: async (
    id: string,
    data: UpdateSalesPointData
  ): Promise<SalesPoint> => {
    const response = await api.put(`/sales-points/${id}`, data);
    return response.data.data;
  },

  deleteSalesPoint: async (id: string): Promise<void> => {
    await api.delete(`/sales-points/${id}`);
  },

  getSalesPointStats: async (id: string): Promise<SalesPointStats> => {
    const response = await api.get(`/sales-points/${id}/stats`);
    return response.data.data;
  },

  getSalesPointsByBranch: async (branchId: string): Promise<SalesPoint[]> => {
    const response = await api.get("/sales-points", {
      params: { branchId },
    });
    return response.data.data.items;
  },
};

export { salesPointAPI };
